import { supabase, supabaseAdmin, isSupabaseConfigured, SubscriptionStatus } from './supabase';
import {
  Organization,
  OrganizationFilters,
  UserFilters,
  DashboardMetrics,
  DateRange,
  AnalyticsDataPoint,
  OrganizationWithSubscription,
  UserWithOrganization,
  AuditLogEntry,
  TrialStats,
  RevenueStats,
} from '../models/admin';

// Mock data for when Supabase is not configured
const MOCK_ORGANIZATIONS: Organization[] = [
  {
    id: '1',
    name: 'Acme Corporation',
    trial_ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    subscription_status: 'trial',
    stripe_customer_id: 'cus_mock1',
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
    mrr: null,
  },
  {
    id: '2',
    name: 'TechStart Inc',
    trial_ends_at: null,
    subscription_status: 'active',
    stripe_customer_id: 'cus_mock2',
    created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
    mrr: 199,
  },
  {
    id: '3',
    name: 'Global Solutions Ltd',
    trial_ends_at: null,
    subscription_status: 'active',
    stripe_customer_id: 'cus_mock3',
    created_at: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
    mrr: 499,
  },
  {
    id: '4',
    name: 'Startup Ventures',
    trial_ends_at: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    subscription_status: 'trial',
    stripe_customer_id: 'cus_mock4',
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
    mrr: null,
  },
  {
    id: '5',
    name: 'Enterprise Co',
    trial_ends_at: null,
    subscription_status: 'past_due',
    stripe_customer_id: 'cus_mock5',
    created_at: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
    mrr: 999,
  },
];

/**
 * Get all organizations with optional filters
 */
export async function getOrganizations(
  filters: OrganizationFilters = {}
): Promise<{ data: Organization[]; count: number }> {
  if (!isSupabaseConfigured()) {
    // Return mock data
    let filtered = [...MOCK_ORGANIZATIONS];

    if (filters.status) {
      filtered = filtered.filter((org) => org.subscription_status === filters.status);
    }

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter((org) => org.name.toLowerCase().includes(search));
    }

    if (filters.sortBy) {
      filtered.sort((a, b) => {
        const aVal = a[filters.sortBy!] || '';
        const bVal = b[filters.sortBy!] || '';
        if (filters.sortOrder === 'desc') {
          return aVal < bVal ? 1 : -1;
        }
        return aVal > bVal ? 1 : -1;
      });
    }

    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const start = (page - 1) * limit;
    const end = start + limit;

    return {
      data: filtered.slice(start, end),
      count: filtered.length,
    };
  }

  let query = supabase.from('organizations').select('*', { count: 'exact' });

  if (filters.status) {
    query = query.eq('subscription_status', filters.status);
  }

  if (filters.search) {
    query = query.ilike('name', `%${filters.search}%`);
  }

  if (filters.sortBy) {
    query = query.order(filters.sortBy, { ascending: filters.sortOrder !== 'desc' });
  }

  if (filters.page && filters.limit) {
    const start = (filters.page - 1) * filters.limit;
    query = query.range(start, start + filters.limit - 1);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching organizations:', error);
    throw error;
  }

  return { data: data || [], count: count || 0 };
}

/**
 * Get organization by ID with subscription details
 */
export async function getOrganizationById(id: string): Promise<OrganizationWithSubscription | null> {
  if (!isSupabaseConfigured()) {
    const org = MOCK_ORGANIZATIONS.find((o) => o.id === id);
    if (!org) return null;

    return {
      ...org,
      subscription: org.subscription_status === 'active' || org.subscription_status === 'past_due'
        ? {
            id: 'sub_' + id,
            organization_id: id,
            plan_id: 'plan_standard',
            status: org.subscription_status,
            current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            mrr: org.mrr || 0,
            stripe_subscription_id: 'sub_stripe_' + id,
            created_at: org.created_at,
            updated_at: org.updated_at,
          }
        : undefined,
      userCount: Math.floor(Math.random() * 20) + 1,
      owner: {
        id: 'user_' + id,
        email: `owner${id}@example.com`,
        full_name: `Owner ${id}`,
      },
    };
  }

  const { data, error } = await supabase
    .from('organizations')
    .select(
      `
      *,
      subscription:subscriptions(*),
      users(count),
      owner:users!organizations_owner_id_fkey(id, email, full_name)
    `
    )
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching organization:', error);
    return null;
  }

  return data;
}

/**
 * Get all users with optional filters
 */
export async function getUsers(
  filters: UserFilters = {}
): Promise<{ data: UserWithOrganization[]; count: number }> {
  if (!isSupabaseConfigured()) {
    // Return mock data
    const mockUsers: UserWithOrganization[] = [
      {
        id: '1',
        email: 'john@acme.com',
        full_name: 'John Doe',
        organization_id: '1',
        role: 'owner',
        created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date().toISOString(),
        organization: {
          id: '1',
          name: 'Acme Corporation',
          subscription_status: 'trial',
        },
      },
      {
        id: '2',
        email: 'jane@techstart.com',
        full_name: 'Jane Smith',
        organization_id: '2',
        role: 'owner',
        created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
        updated_at: new Date().toISOString(),
        organization: {
          id: '2',
          name: 'TechStart Inc',
          subscription_status: 'active',
        },
      },
    ];

    return { data: mockUsers, count: mockUsers.length };
  }

  let query = supabase
    .from('users')
    .select('*, organization:organizations(id, name, subscription_status)', { count: 'exact' });

  if (filters.search) {
    query = query.or(`full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
  }

  if (filters.organizationId) {
    query = query.eq('organization_id', filters.organizationId);
  }

  if (filters.role) {
    query = query.eq('role', filters.role);
  }

  if (filters.sortBy) {
    query = query.order(filters.sortBy, { ascending: filters.sortOrder !== 'desc' });
  }

  if (filters.page && filters.limit) {
    const start = (filters.page - 1) * filters.limit;
    query = query.range(start, start + filters.limit - 1);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching users:', error);
    throw error;
  }

  return { data: data || [], count: count || 0 };
}

/**
 * Get dashboard analytics metrics
 */
export async function getDashboardMetrics(): Promise<DashboardMetrics> {
  if (!isSupabaseConfigured()) {
    // Return mock metrics
    const activeTrials = MOCK_ORGANIZATIONS.filter((org) => org.subscription_status === 'trial').length;
    const activeSubscriptions = MOCK_ORGANIZATIONS.filter(
      (org) => org.subscription_status === 'active'
    ).length;
    const totalMRR = MOCK_ORGANIZATIONS.reduce((sum, org) => sum + (org.mrr || 0), 0);

    return {
      totalOrganizations: MOCK_ORGANIZATIONS.length,
      activeTrials,
      activeSubscriptions,
      monthlyRecurringRevenue: totalMRR,
      trialConversionRate: 42.5,
      churnedThisMonth: 2,
    };
  }

  // Fetch from Supabase
  const [orgsResult, trialsResult, subscriptionsResult, mrrResult] = await Promise.all([
    supabase.from('organizations').select('id', { count: 'exact', head: true }),
    supabase
      .from('organizations')
      .select('id', { count: 'exact', head: true })
      .eq('subscription_status', 'trial'),
    supabase
      .from('organizations')
      .select('id', { count: 'exact', head: true })
      .eq('subscription_status', 'active'),
    supabase.from('subscriptions').select('mrr'),
  ]);

  const totalMRR = mrrResult.data?.reduce((sum, sub) => sum + (sub.mrr || 0), 0) || 0;

  return {
    totalOrganizations: orgsResult.count || 0,
    activeTrials: trialsResult.count || 0,
    activeSubscriptions: subscriptionsResult.count || 0,
    monthlyRecurringRevenue: totalMRR,
    trialConversionRate: 0, // Calculate based on historical data
    churnedThisMonth: 0, // Calculate based on cancellations this month
  };
}

/**
 * Get analytics data for a date range
 */
export async function getAnalytics(dateRange: DateRange): Promise<{
  signups: AnalyticsDataPoint[];
  conversions: AnalyticsDataPoint[];
  revenue: AnalyticsDataPoint[];
}> {
  if (!isSupabaseConfigured()) {
    // Generate mock analytics data
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const signups: AnalyticsDataPoint[] = [];
    const conversions: AnalyticsDataPoint[] = [];
    const revenue: AnalyticsDataPoint[] = [];

    for (let i = 0; i < days; i++) {
      const date = new Date(dateRange.start);
      date.setDate(date.getDate() + i);
      signups.push({
        date: date.toISOString().split('T')[0],
        value: Math.floor(Math.random() * 10) + 1,
      });
      conversions.push({
        date: date.toISOString().split('T')[0],
        value: Math.floor(Math.random() * 5),
      });
      revenue.push({
        date: date.toISOString().split('T')[0],
        value: Math.floor(Math.random() * 5000) + 1000,
      });
    }

    return { signups, conversions, revenue };
  }

  // Fetch analytics from Supabase
  // This would involve complex queries based on your schema
  return {
    signups: [],
    conversions: [],
    revenue: [],
  };
}

/**
 * Extend trial for an organization
 */
export async function extendTrial(orgId: string, days: number, adminUserId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    console.log(`Mock: Extending trial for org ${orgId} by ${days} days`);
    return;
  }

  const client = supabaseAdmin || supabase;

  // Get current organization
  const { data: org, error: fetchError } = await client
    .from('organizations')
    .select('trial_ends_at')
    .eq('id', orgId)
    .single();

  if (fetchError) throw fetchError;

  // Calculate new trial end date
  const currentEnd = org.trial_ends_at ? new Date(org.trial_ends_at) : new Date();
  const newEnd = new Date(currentEnd.getTime() + days * 24 * 60 * 60 * 1000);

  // Update organization
  const { error: updateError } = await client
    .from('organizations')
    .update({ trial_ends_at: newEnd.toISOString() })
    .eq('id', orgId);

  if (updateError) throw updateError;

  // Log the action
  await logAdminAction(adminUserId, 'trial_extended', 'organization', orgId, {
    days_added: days,
    new_trial_end: newEnd.toISOString(),
  });
}

/**
 * Update subscription status
 */
export async function updateSubscriptionStatus(
  orgId: string,
  status: SubscriptionStatus,
  adminUserId: string
): Promise<void> {
  if (!isSupabaseConfigured()) {
    console.log(`Mock: Updating subscription status for org ${orgId} to ${status}`);
    return;
  }

  const client = supabaseAdmin || supabase;

  const { error } = await client
    .from('organizations')
    .update({ subscription_status: status })
    .eq('id', orgId);

  if (error) throw error;

  // Log the action
  await logAdminAction(adminUserId, 'subscription_updated', 'organization', orgId, {
    new_status: status,
  });
}

/**
 * Get audit log for an entity
 */
export async function getAuditLog(entityId: string, entityType: string): Promise<AuditLogEntry[]> {
  if (!isSupabaseConfigured()) {
    // Return mock audit log
    return [
      {
        id: '1',
        admin_user_id: 'admin1',
        admin_name: 'Admin User',
        action: 'trial_extended',
        entity_type: 'organization',
        entity_id: entityId,
        changes: { days_added: 14 },
        created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ];
  }

  const { data, error } = await supabase
    .from('admin_audit_log')
    .select(
      `
      *,
      admin:admin_users!admin_audit_log_admin_user_id_fkey(
        user:users(full_name)
      )
    `
    )
    .eq('entity_id', entityId)
    .eq('entity_type', entityType)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching audit log:', error);
    return [];
  }

  return (data || []).map((entry: any) => ({
    ...entry,
    admin_name: entry.admin?.user?.full_name || 'Unknown Admin',
  }));
}

/**
 * Log an admin action
 */
export async function logAdminAction(
  adminUserId: string,
  action: string,
  entityType: string,
  entityId: string,
  changes: Record<string, any>
): Promise<void> {
  if (!isSupabaseConfigured()) {
    console.log('Mock: Logging admin action', { adminUserId, action, entityType, entityId, changes });
    return;
  }

  const client = supabaseAdmin || supabase;

  const { error } = await client.from('admin_audit_log').insert({
    admin_user_id: adminUserId,
    action,
    entity_type: entityType,
    entity_id: entityId,
    changes,
  });

  if (error) {
    console.error('Error logging admin action:', error);
  }
}

/**
 * Get trial statistics
 */
export async function getTrialStats(): Promise<TrialStats> {
  if (!isSupabaseConfigured()) {
    return {
      totalActive: 2,
      expiringSoon: 1,
      converted: 15,
      canceled: 3,
      averageDuration: 14,
    };
  }

  const now = new Date();
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  const [activeResult, expiringSoonResult] = await Promise.all([
    supabase
      .from('organizations')
      .select('id', { count: 'exact', head: true })
      .eq('subscription_status', 'trial'),
    supabase
      .from('organizations')
      .select('id', { count: 'exact', head: true })
      .eq('subscription_status', 'trial')
      .lte('trial_ends_at', sevenDaysFromNow.toISOString()),
  ]);

  return {
    totalActive: activeResult.count || 0,
    expiringSoon: expiringSoonResult.count || 0,
    converted: 0, // Calculate from historical data
    canceled: 0, // Calculate from historical data
    averageDuration: 14,
  };
}

/**
 * Get revenue statistics
 */
export async function getRevenueStats(): Promise<RevenueStats> {
  if (!isSupabaseConfigured()) {
    return {
      currentMRR: 1697,
      previousMRR: 1598,
      growth: 6.2,
      newMRR: 199,
      churnedMRR: 100,
    };
  }

  const { data, error } = await supabase.from('subscriptions').select('mrr, created_at');

  if (error) {
    console.error('Error fetching revenue stats:', error);
    return {
      currentMRR: 0,
      previousMRR: 0,
      growth: 0,
      newMRR: 0,
      churnedMRR: 0,
    };
  }

  const currentMRR = data?.reduce((sum, sub) => sum + (sub.mrr || 0), 0) || 0;

  return {
    currentMRR,
    previousMRR: 0, // Calculate from previous month
    growth: 0,
    newMRR: 0,
    churnedMRR: 0,
  };
}

/**
 * Export organizations to CSV
 */
export function exportOrganizationsToCSV(organizations: Organization[]): string {
  const headers = ['ID', 'Name', 'Status', 'Trial Ends', 'MRR', 'Created At'];
  const rows = organizations.map((org) => [
    org.id,
    org.name,
    org.subscription_status,
    org.trial_ends_at || 'N/A',
    org.mrr?.toString() || 'N/A',
    new Date(org.created_at).toLocaleDateString(),
  ]);

  const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
  return csv;
}

/**
 * Download CSV file
 */
export function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
